import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatDisplayName } from '@/lib/formatName';

export interface AuditPolicyData {
  id: string;
  name: string;
  category: string;
  status: string;
  effective_date: string;
  next_review_date: string;
  last_review_date: string | null;
  review_cadence_months: number;
  requires_acknowledgement: boolean;
  owner_name: string;
  owner_email: string;
  versions: AuditVersionData[];
}

export interface AuditVersionData {
  id: string;
  version_number: number;
  version_status: string;
  is_substantive: boolean;
  created_at: string;
  created_by_name: string;
  approved_by_name: string | null;
  rejection_reason: string | null;
  rejected_by_name: string | null;
  acknowledgements: AuditAcknowledgementData[];
}

export interface AuditAcknowledgementData {
  id: string;
  acknowledged_at: string;
  acknowledged_by_name: string;
  acknowledged_by_email: string;
  version_status: string;
}

export interface AuditOwnershipChange {
  id: string;
  policy_id: string;
  policy_name: string;
  previous_owner_name: string | null;
  new_owner_name: string;
  changed_by_name: string;
  changed_at: string;
}

export type ComplianceStatus = 'overdue' | 'due_soon' | 'needs_ack' | 'current';

export function getComplianceStatus(
  policy: AuditPolicyData,
  pendingAckCount: number
): ComplianceStatus {
  const today = new Date();
  const nextReview = new Date(policy.next_review_date);
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(today.getDate() + 30);

  if (nextReview < today) {
    return 'overdue';
  }
  if (nextReview <= thirtyDaysFromNow) {
    return 'due_soon';
  }
  if (policy.requires_acknowledgement && pendingAckCount > 0) {
    return 'needs_ack';
  }
  return 'current';
}

export function getComplianceStatusLabel(status: ComplianceStatus): string {
  switch (status) {
    case 'overdue':
      return 'Over Due';
    case 'due_soon':
      return 'Due Soon';
    case 'needs_ack':
      return 'Needs Ack';
    case 'current':
      return 'Current';
  }
}

// Valid database enum values for policy_status
const DB_STATUSES = ['Approved', 'draft', 'pending_approval', 'expired'];
// Calculated statuses that require client-side date logic
const CALCULATED_STATUSES = ['review_overdue', 'review_due_soon', 'review_need_ack'];

export function useAuditData(filters?: { categories?: string[]; statuses?: string[] }) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['audit-data', profile?.clinic_id, filters?.categories, filters?.statuses],
    queryFn: async () => {
      if (!profile) throw new Error('No profile found');
      const t0 = performance.now();

      // Separate DB statuses from calculated statuses
      const dbStatuses = filters?.statuses?.filter(s => DB_STATUSES.includes(s)) ?? [];
      const calcStatuses = filters?.statuses?.filter(s => CALCULATED_STATUSES.includes(s)) ?? [];
      const hasStatusFilter = (filters?.statuses?.length ?? 0) > 0;

      // Fetch policies with owner info
      let policiesQuery = supabase
        .from('policies')
        .select(`
          id,
          name,
          category,
          status,
          effective_date,
          next_review_date,
          last_review_date,
          review_cadence_months,
          requires_acknowledgement,
          owner_profile:profiles!policies_policy_owner_fkey(full_name, email, credentials)
        `)
        .eq('clinic_id', profile.clinic_id)
        .order('name');

      if (filters?.categories && filters.categories.length > 0) {
        policiesQuery = policiesQuery.in('category', filters.categories);
      }
      // Only apply DB-level status filter when there are NO calculated statuses
      // (if mixed, we need all policies to evaluate calculated statuses client-side)
      if (dbStatuses.length > 0 && calcStatuses.length === 0) {
        policiesQuery = policiesQuery.in('status', dbStatuses as ('Approved')[]);
      }

      const { data: policies, error: policiesError } = await policiesQuery;
      if (policiesError) throw policiesError;

      const policyIds = policies?.map(p => p.id) ?? [];
      const safePolicyIds = policyIds.length > 0 ? policyIds : [''];

      console.log(`[audit] policies fetched in ${(performance.now() - t0).toFixed(0)}ms, count=${policyIds.length}`);

      // Fetch versions and ownership history in parallel (both only need policyIds)
      const t1 = performance.now();
      const [versionsResult, historyResult] = await Promise.all([
        supabase
          .from('policy_versions')
          .select(`
            id,
            policy_id,
            version_number,
            version_status,
            is_substantive,
            created_at,
            rejection_reason,
            created_by_profile:profiles!policy_versions_created_by_fkey(full_name, credentials),
            approved_by_profile:profiles!policy_versions_approved_by_fkey(full_name, credentials),
            rejected_by_profile:profiles!policy_versions_rejected_by_fkey(full_name, credentials)
          `)
          .in('policy_id', safePolicyIds)
          .order('version_number', { ascending: false }),
        supabase
          .from('policy_ownership_history')
          .select(`
            id,
            policy_id,
            changed_at,
            previous_owner_profile:profiles!policy_ownership_history_previous_owner_fkey(full_name, credentials),
            new_owner_profile:profiles!policy_ownership_history_new_owner_fkey(full_name, credentials),
            changed_by_profile:profiles!policy_ownership_history_changed_by_fkey(full_name, credentials)
          `)
          .in('policy_id', safePolicyIds)
          .order('changed_at', { ascending: false }),
      ]);

      if (versionsResult.error) throw versionsResult.error;
      if (historyResult.error) throw historyResult.error;
      const versions = versionsResult.data;
      const ownershipHistory = historyResult.data;

      console.log(`[audit] versions+ownership fetched in ${(performance.now() - t1).toFixed(0)}ms, versions=${versions?.length}, ownership=${ownershipHistory?.length}`);

      const versionIds = versions?.map(v => v.id) ?? [];

      // Fetch acknowledgements (needs versionIds from above)
      const t2 = performance.now();
      const { data: acknowledgements, error: acksError } = await supabase
        .from('policy_acknowledgements')
        .select(`
          id,
          policy_version_id,
          acknowledged_at,
          acknowledged_by_profile:profiles!policy_acknowledgements_acknowledged_by_fkey(full_name, email, credentials)
        `)
        .in('policy_version_id', versionIds.length > 0 ? versionIds : [''])
        .order('acknowledged_at', { ascending: false });

      if (acksError) throw acksError;

      console.log(`[audit] acks fetched in ${(performance.now() - t2).toFixed(0)}ms, count=${acknowledgements?.length}`);

      // Create a map of version ID to version status
      const versionStatusMap = new Map<string, string>();
      versions?.forEach(v => {
        versionStatusMap.set(v.id, v.version_status);
      });

      // Map versions to their acknowledgements
      const versionAcksMap = new Map<string, AuditAcknowledgementData[]>();
      acknowledgements?.forEach(ack => {
        const existing = versionAcksMap.get(ack.policy_version_id) || [];
        existing.push({
          id: ack.id,
          acknowledged_at: ack.acknowledged_at,
          acknowledged_by_name: formatDisplayName((ack.acknowledged_by_profile as any)?.full_name ?? 'Unknown', (ack.acknowledged_by_profile as any)?.credentials),
          acknowledged_by_email: (ack.acknowledged_by_profile as any)?.email ?? '',
          version_status: versionStatusMap.get(ack.policy_version_id) ?? 'unknown',
        });
        versionAcksMap.set(ack.policy_version_id, existing);
      });

      // Map policies to their versions
      const policyVersionsMap = new Map<string, AuditVersionData[]>();
      versions?.forEach(v => {
        const existing = policyVersionsMap.get(v.policy_id) || [];
        existing.push({
          id: v.id,
          version_number: v.version_number,
          version_status: v.version_status,
          is_substantive: v.is_substantive,
          created_at: v.created_at,
          created_by_name: formatDisplayName((v.created_by_profile as any)?.full_name ?? 'Unknown', (v.created_by_profile as any)?.credentials),
          approved_by_name: (v.approved_by_profile as any)?.full_name ? formatDisplayName((v.approved_by_profile as any).full_name, (v.approved_by_profile as any).credentials) : null,
          rejection_reason: v.rejection_reason,
          rejected_by_name: (v.rejected_by_profile as any)?.full_name ? formatDisplayName((v.rejected_by_profile as any).full_name, (v.rejected_by_profile as any).credentials) : null,
          acknowledgements: versionAcksMap.get(v.id) || [],
        });
        policyVersionsMap.set(v.policy_id, existing);
      });

      // Build final audit data
      let auditPolicies: AuditPolicyData[] = (policies ?? []).map(p => ({
        id: p.id,
        name: p.name,
        category: p.category,
        status: p.status,
        effective_date: p.effective_date,
        next_review_date: p.next_review_date,
        last_review_date: p.last_review_date,
        review_cadence_months: p.review_cadence_months,
        requires_acknowledgement: p.requires_acknowledgement,
        owner_name: formatDisplayName((p.owner_profile as any)?.full_name ?? 'Unknown', (p.owner_profile as any)?.credentials),
        owner_email: (p.owner_profile as any)?.email ?? '',
        versions: policyVersionsMap.get(p.id) || [],
      }));

      // Apply client-side filtering for calculated and/or mixed statuses
      if (hasStatusFilter && (calcStatuses.length > 0 || dbStatuses.length > 0)) {
        const todayStr = new Date().toISOString().split('T')[0];
        const thirtyDays = new Date();
        thirtyDays.setDate(thirtyDays.getDate() + 30);
        const thirtyDaysStr = thirtyDays.toISOString().split('T')[0];

        auditPolicies = auditPolicies.filter(p => {
          // Check if policy matches any of the selected statuses (OR logic)
          return (dbStatuses.length > 0 && dbStatuses.includes(p.status)) ||
            (calcStatuses.includes('review_overdue') && p.next_review_date && p.next_review_date < todayStr && p.status === 'Approved') ||
            (calcStatuses.includes('review_due_soon') && p.next_review_date && p.next_review_date >= todayStr && p.next_review_date <= thirtyDaysStr && p.status === 'Approved') ||
            (calcStatuses.includes('review_need_ack') && p.requires_acknowledgement && p.status === 'Approved' && p.versions.some(v => v.version_status === 'approved' && v.acknowledgements.length === 0));
        });
      }

      // Build ownership changes with policy names
      const policyNameMap = new Map(policies?.map(p => [p.id, p.name]) ?? []);
      const ownershipChanges: AuditOwnershipChange[] = (ownershipHistory ?? [])
        .filter(h => policyNameMap.has(h.policy_id))
        .map(h => ({
          id: h.id,
          policy_id: h.policy_id,
          policy_name: policyNameMap.get(h.policy_id) ?? 'Unknown',
          previous_owner_name: (h.previous_owner_profile as any)?.full_name ? formatDisplayName((h.previous_owner_profile as any).full_name, (h.previous_owner_profile as any).credentials) : null,
          new_owner_name: formatDisplayName((h.new_owner_profile as any)?.full_name ?? 'Unknown', (h.new_owner_profile as any)?.credentials),
          changed_by_name: formatDisplayName((h.changed_by_profile as any)?.full_name ?? 'Unknown', (h.changed_by_profile as any)?.credentials),
          changed_at: h.changed_at,
        }));

      console.log(`[audit] total query time: ${(performance.now() - t0).toFixed(0)}ms, policies=${auditPolicies.length}, statuses=${JSON.stringify(filters?.statuses)}`);

      return {
        policies: auditPolicies,
        ownershipChanges,
      };
    },
    enabled: !!profile,
  });
}
